// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useEffect, useRef, useState } from 'react';
import { consoleService } from '../../services/consoleService';
import { LeaderboardRecord } from '../../types/leaderboards';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import { 
  QuizManagementDashboard,
  Question,
  QuestionCategory,
  QuestionDifficulty,
  QuestionType,
  QuestionStatus,
  CreateQuestionRequest,
  serializeDifficulty,
  QuestionCategoryInfo
} from '../../types/quizManagement';
import './QuizManagement.scss';

interface QuizImportRow {
  category_id: string;
  category_name: string;
  difficulty: number;
  question_id: string;
  question_text: string;
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  correct_option_index: number;
  question_timer_sec: number;
  hint_text: string;
  is_active: boolean;
}

interface QuizImportSummary {
  totalRows: number;
  importedRows: number;
  failedRows: number;
  failures: Array<{ row: number; message: string }>;
}

const QUIZ_IMPORT_TEMPLATE_PATH = '/templates/quiz_questions_import_template.xlsx';

const QuizManagement: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<QuizManagementDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'questions' | 'categories' | 'leaderboard'>('questions');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Leaderboard state
  const [leaderboardRecords, setLeaderboardRecords] = useState<LeaderboardRecord[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();
  const QUIZ_LEADERBOARD_ID = 'total_earned_coins_quiz';

  // Category modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDifficulty, setNewCategoryDifficulty] = useState<number>(0); // 0=Easy, 1=Medium, 2=Hard

  // Store backend categories for dropdown (categoryId -> categoryName mapping)
  const [backendCategories, setBackendCategories] = useState<Record<string, { categoryId: string; categoryName: string; difficulty: number }>>({});
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  // Filter state for Question Bank
  const [filterCategoryId, setFilterCategoryId] = useState<string | null>(null);
  const [isImportingQuestions, setIsImportingQuestions] = useState(false);
  const [importSummary, setImportSummary] = useState<QuizImportSummary | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Form states
  const [questionForm, setQuestionForm] = useState<Partial<CreateQuestionRequest>>({
    question: '',
    type: QuestionType.MULTIPLE_CHOICE,
    category: QuestionCategory.GENERAL_KNOWLEDGE,
    difficulty: QuestionDifficulty.EASY,
    options: [
      { text: '', isCorrect: true },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ],
    points: 10,
    timeLimit: 30,
    tags: [],
    language: 'en'
  });
  const [questionId, setQuestionId] = useState('');
  const [hintText, setHintText] = useState('');

  const parseBooleanCell = (value: unknown, defaultValue = true): boolean => {
    if (value === undefined || value === null || value === '') return defaultValue;
    if (typeof value === 'boolean') return value;
    const normalized = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    return defaultValue;
  };

  const parseNumberCell = (value: unknown, fallback: number): number => {
    if (value === undefined || value === null || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const fromBackendCorrectOptionIndex = (value: unknown): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 1;

    // The quiz runtime stores zero-based indexes. Convert them back to the
    // 1-based values that the admin panel shows to users.
    if (parsed >= 0 && parsed <= 3) return parsed + 1;
    if (parsed >= 1 && parsed <= 4) return parsed;

    return 1;
  };

  const normalizeImportRows = (rows: Record<string, unknown>[]): QuizImportRow[] => {
    return rows.map((row) => ({
      category_id: String(row.category_id ?? '').trim(),
      category_name: String(row.category_name ?? '').trim(),
      difficulty: parseNumberCell(row.difficulty, 0),
      question_id: String(row.question_id ?? '').trim(),
      question_text: String(row.question_text ?? '').trim(),
      option1: String(row.option1 ?? '').trim(),
      option2: String(row.option2 ?? '').trim(),
      option3: String(row.option3 ?? '').trim(),
      option4: String(row.option4 ?? '').trim(),
      correct_option_index: parseNumberCell(row.correct_option_index, 0),
      question_timer_sec: parseNumberCell(row.question_timer_sec, 30),
      hint_text: String(row.hint_text ?? '').trim(),
      is_active: parseBooleanCell(row.is_active, true),
    }));
  };

  const validateImportRow = (row: QuizImportRow): string | null => {
    if (!row.category_id) return 'category_id is required';
    if (!row.question_text) return 'question_text is required';
    if (!row.option1 || !row.option2 || !row.option3 || !row.option4) return 'option1-option4 are required';
    if (![0, 1, 2].includes(row.difficulty)) return 'difficulty must be 0, 1, or 2';
    if (![1, 2, 3, 4].includes(row.correct_option_index)) return 'correct_option_index must be 1-4';
    if (row.question_timer_sec < 5) return 'question_timer_sec must be at least 5';
    return null;
  };

  const handleImportQuestionsFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImportingQuestions(true);
    setImportSummary(null);
    setError(null);
    setSuccessMessage(null);

    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        throw new Error('The uploaded workbook has no sheets.');
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
        defval: '',
      });

      if (rawRows.length === 0) {
        throw new Error('The uploaded file is empty. Please use the template and add at least one row.');
      }

      const rows = normalizeImportRows(rawRows).filter((row) =>
        Object.values(row).some((value) => String(value).trim() !== '')
      );

      if (rows.length === 0) {
        throw new Error('No valid rows found in the uploaded file.');
      }

      const knownCategoryIds = new Set(Object.keys(backendCategories));
      const failures: Array<{ row: number; message: string }> = [];
      let importedRows = 0;

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const rowNumber = i + 2; // Excel row number, header is row 1

        const validationError = validateImportRow(row);
        if (validationError) {
          failures.push({ row: rowNumber, message: validationError });
          continue;
        }

        try {
          if (!knownCategoryIds.has(row.category_id)) {
            if (!row.category_name) {
              throw new Error(`category_id "${row.category_id}" not found and category_name is missing`);
            }

            await consoleService.addQuizCategory({
              categoryId: row.category_id,
              categoryName: row.category_name,
              difficulty: row.difficulty,
              isActive: true,
            });

            knownCategoryIds.add(row.category_id);
          }

          await consoleService.addQuizQuestionToCategory({
            hint: row.hint_text ? { hintText: row.hint_text } : undefined,
            option1: row.option1,
            option2: row.option2,
            option3: row.option3,
            option4: row.option4,
            isActive: row.is_active,
            categoryId: row.category_id,
            difficulty: row.difficulty,
            questionId: row.question_id || `q_import_${Date.now()}_${i + 1}`,
            questionText: row.question_text,
            questionTimerSec: row.question_timer_sec,
            correctOptionIndex: row.correct_option_index,
          });

          importedRows++;
        } catch (rowError) {
          const message =
            rowError instanceof Error ? rowError.message : 'Unknown import error';
          failures.push({ row: rowNumber, message });
        }
      }

      const summary: QuizImportSummary = {
        totalRows: rows.length,
        importedRows,
        failedRows: failures.length,
        failures,
      };
      setImportSummary(summary);

      if (importedRows > 0) {
        setSuccessMessage(
          `Imported ${importedRows}/${rows.length} question(s) from "${file.name}".`
        );
        await fetchQuizManagementData();
      }

      if (failures.length > 0 && importedRows === 0) {
        setError('Import failed. Fix the row errors and try again.');
      }
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : 'Failed to import questions');
    } finally {
      setIsImportingQuestions(false);
      event.target.value = '';
    }
  };

  // Fetch quiz management data - syncs from quiz_categories_get and quiz_question_get_by_category
  const fetchQuizManagementData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await consoleService.getQuizManagementDashboard();
      
      // Step 1: Fetch categories from quiz_categories_get endpoint
      let categoriesResponse: any = null;
      let realCategories: QuestionCategoryInfo[] = [];
      try {
        const rpcResponse = await consoleService.callRpcFunction({
          id: 'quiz_categories_get',
          body: JSON.stringify({}),
          user_id: '00000000-0000-0000-0000-000000000000',
          http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
        });

        if (rpcResponse.body) {
          categoriesResponse = JSON.parse(rpcResponse.body);
          // Store backend categories for dropdown
          if (categoriesResponse.categories) {
            setBackendCategories(categoriesResponse.categories);
          }
          // Map to frontend format
          realCategories = await consoleService.getQuizCategories();
          if (realCategories && realCategories.length > 0) {
            data.categories = realCategories;
          }
        }
      } catch (categoryError) {
        console.warn('Failed to fetch categories from quiz_categories_get:', categoryError);
        // Try fallback
        try {
          realCategories = await consoleService.getQuizCategories();
          if (realCategories && realCategories.length > 0) {
            data.categories = realCategories;
          }
        } catch (fallbackError) {
          console.warn('Failed to fetch real categories, using dashboard categories:', fallbackError);
        }
      }
      
      // Step 2: Fetch questions from quiz_question_get_by_category for each categoryId
      try {
        const allQuestions: Question[] = [];
        
        if (categoriesResponse && categoriesResponse.categories) {
          const categoryIds = Object.keys(categoriesResponse.categories);
          
          // Fetch questions for each categoryId using quiz_question_get_by_category
          for (const categoryId of categoryIds) {
            try {
              const questionsResponse = await consoleService.getQuizQuestionsByCategory({
                category_id: categoryId
              });
              
              console.log(`Raw response for category ${categoryId}:`, questionsResponse);
              
              // Handle different response structures
              // Backend returns: { Questions: { "q_0": {...}, "q_1": {...} } }
              let questionsArray: any[] = [];
              if (questionsResponse.Questions && typeof questionsResponse.Questions === 'object') {
                // Backend format: { Questions: { questionId: QuestionData } }
                questionsArray = Object.values(questionsResponse.Questions);
              } else if (Array.isArray(questionsResponse)) {
                questionsArray = questionsResponse;
              } else if (questionsResponse.questions && typeof questionsResponse.questions === 'object') {
                questionsArray = Object.values(questionsResponse.questions);
              } else if (questionsResponse.data && Array.isArray(questionsResponse.data)) {
                questionsArray = questionsResponse.data;
              } else if (typeof questionsResponse === 'object' && questionsResponse !== null) {
                // If response is an object with question keys, convert to array
                const values = Object.values(questionsResponse);
                // Check if values are question objects (have questionId or questionText)
                if (values.length > 0 && typeof values[0] === 'object' && values[0] !== null) {
                  const firstVal = values[0] as any;
                  if (firstVal.questionId || firstVal.questionText) {
                    questionsArray = values;
                  }
                }
              }
              
              console.log(`Category ${categoryId}: Found ${questionsArray.length} questions`, questionsArray);
              
              // Map backend questions to frontend format - process all questions even if array seems empty
              questionsArray.forEach((backendQuestion: any) => {
                if (!backendQuestion) return; // Skip null/undefined entries
                
                const categoryInfo = categoriesResponse.categories[categoryId];
                  // Try multiple field names for category name
                  const categoryName = categoryInfo?.categoryName || categoryInfo?.name || categoryInfo?.category_name || categoryId.split('_')[0] || '';
                  
                  console.log(`Category info for ${categoryId}:`, categoryInfo, 'Resolved name:', categoryName);
                  
                  // Map category name to QuestionCategory enum (case-insensitive)
                  const lowerCategoryName = categoryName.toLowerCase();
                  const categoryMap: Record<string, QuestionCategory> = {
                    'science': QuestionCategory.SCIENCE,
                    'sport': QuestionCategory.SPORTS,
                    'sports': QuestionCategory.SPORTS,
                    'space': QuestionCategory.SCIENCE,
                    'technology': QuestionCategory.TECHNOLOGY,
                    'tech': QuestionCategory.TECHNOLOGY,
                    'history': QuestionCategory.HISTORY,
                    'geography': QuestionCategory.GEOGRAPHY,
                    'geo': QuestionCategory.GEOGRAPHY,
                    'entertainment': QuestionCategory.ENTERTAINMENT,
                    'literature': QuestionCategory.LITERATURE,
                    'mathematics': QuestionCategory.MATHEMATICS,
                    'math': QuestionCategory.MATHEMATICS,
                    'politics': QuestionCategory.POLITICS,
                    'general knowledge': QuestionCategory.GENERAL_KNOWLEDGE,
                    'general': QuestionCategory.GENERAL_KNOWLEDGE
                  };
                  
                  const frontendCategory = categoryMap[lowerCategoryName] || QuestionCategory.GENERAL_KNOWLEDGE;
                  
                  // Map difficulty (0/1/2 to EASY/MEDIUM/HARD)
                  // First try to get difficulty from the question itself, then fall back to category difficulty
                  const difficultyMap: Record<number, QuestionDifficulty> = {
                    0: QuestionDifficulty.EASY,
                    1: QuestionDifficulty.MEDIUM,
                    2: QuestionDifficulty.HARD
                  };
                  
                  // Get difficulty from question first, then category, default to EASY
                  const questionDifficulty = backendQuestion.difficulty ?? backendQuestion.Difficulty ?? categoryInfo?.difficulty ?? 0;
                  const frontendDifficulty = difficultyMap[questionDifficulty] || QuestionDifficulty.EASY;
                  
                  console.log(`Question ${backendQuestion.questionId}: difficulty from question=${backendQuestion.difficulty}, from category=${categoryInfo?.difficulty}, resolved=${questionDifficulty}`);
                  
                  // Map backend question to frontend Question format
                  const questionId = backendQuestion.questionId || backendQuestion.question_id || `q_${Date.now()}_${Math.random()}`;
                  const correctOptionIndex = fromBackendCorrectOptionIndex(
                    backendQuestion.correctOptionIndex ?? backendQuestion.correct_option_index
                  );
                  const frontendQuestion: Question & { categoryId?: string; categoryName?: string } = {
                    id: questionId,
                    question: backendQuestion.questionText || backendQuestion.question || '',
                    type: QuestionType.MULTIPLE_CHOICE, // Default, backend doesn't specify
                    category: frontendCategory,
                    difficulty: frontendDifficulty,
                    status: backendQuestion.isActive !== false ? QuestionStatus.ACTIVE : QuestionStatus.INACTIVE,
                    options: [
                      { id: 'opt1', text: backendQuestion.option1 || '', isCorrect: correctOptionIndex === 1 },
                      { id: 'opt2', text: backendQuestion.option2 || '', isCorrect: correctOptionIndex === 2 },
                      { id: 'opt3', text: backendQuestion.option3 || '', isCorrect: correctOptionIndex === 3 },
                      { id: 'opt4', text: backendQuestion.option4 || '', isCorrect: correctOptionIndex === 4 }
                    ],
                    correctAnswer: backendQuestion[`option${correctOptionIndex}`] || '',
                    explanation: backendQuestion.hint?.hintText || '',
                    points: 10, // Default, backend doesn't specify
                    timeLimit: backendQuestion.questionTimerSec || 30,
                    tags: [],
                    language: 'en',
                    createdBy: 'system',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    usageCount: 0,
                    correctAnswerRate: 0,
                    averageResponseTime: 0,
                    categoryId: categoryId, // Store categoryId for deletion
                    categoryName: categoryName // Store actual category name from backend
                  };
                  
                  allQuestions.push(frontendQuestion);
                });
            } catch (questionError) {
              console.warn(`Failed to fetch questions for category ${categoryId} from quiz_question_get_by_category:`, questionError);
              // Continue with other categories
            }
          }
          
          // Update dashboard data with real questions
          data.questions = allQuestions;
          // Update summary counts
          data.summary.totalQuestions = allQuestions.length;
          data.summary.activeQuestions = allQuestions.filter(q => q.status === QuestionStatus.ACTIVE).length;
        } else {
          console.warn('No categories found, cannot fetch questions');
        }
      } catch (questionError) {
        console.warn('Failed to fetch questions from quiz_question_get_by_category, using dashboard questions:', questionError);
        // Keep dashboard questions as fallback
      }
      
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch quiz management data');
    } finally {
      setLoading(false);
    }
  };

  // Create question
  const handleCreateQuestion = async () => {
    try {
      if (!questionForm.question || !questionForm.options || questionForm.options.length < 4) {
        setError('Please fill in all required fields including all 4 options');
        return;
      }

      if (!selectedCategoryId) {
        setError('Please select a category');
        return;
      }

      // Find the correct option index (1-based)
      const correctIndex = questionForm.options.findIndex((opt: { text: string; isCorrect: boolean }) => opt.isCorrect);
      if (correctIndex === -1) {
        setError('Please select a correct answer');
        return;
      }
      const correctOptionIndex = correctIndex + 1; // Convert to 1-based

      // Map difficulty to backend format
      const difficulty = serializeDifficulty(questionForm.difficulty || QuestionDifficulty.EASY);

      // Generate questionId if not provided
      const finalQuestionId = questionId || `q_${Date.now()}`;

      // Build the request using selected category from dropdown
      const addQuestionRequest = {
        hint: hintText ? { hintText } : undefined,
        option1: questionForm.options[0]?.text || '',
        option2: questionForm.options[1]?.text || '',
        option3: questionForm.options[2]?.text || '',
        option4: questionForm.options[3]?.text || '',
        isActive: true,
        categoryId: selectedCategoryId,
        difficulty: difficulty,
        questionId: finalQuestionId,
        questionText: questionForm.question,
        questionTimerSec: questionForm.timeLimit || 30,
        correctOptionIndex: correctOptionIndex
      };

      await consoleService.addQuizQuestionToCategory(addQuestionRequest);
      setShowCreateModal(false);
      resetQuestionForm();
      setSelectedCategoryId('');
      setQuestionId('');
      setHintText('');
      await fetchQuizManagementData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create question');
    }
  };



  // Delete question
  const handleDeleteQuestion = async (questionId: string) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      // Find the question to get its categoryId
      const question = dashboardData?.questions.find(q => q.id === questionId);
      if (!question) {
        setError('Question not found');
        return;
      }

      // Get categoryId from question (stored when fetched) or construct it
      const questionWithCategoryId = question as Question & { categoryId?: string };
      let categoryId = questionWithCategoryId.categoryId;

      // If categoryId not stored, try to construct it
      if (!categoryId) {
        const categoryInfo = getCategoryInfo(question.category);
        const categoryName = categoryInfo.name.toLowerCase().replace(/\s+/g, '_');
        const difficultyName = question.difficulty === QuestionDifficulty.EASY ? 'easy' : 
                              question.difficulty === QuestionDifficulty.MEDIUM ? 'medium' : 'hard';
        categoryId = `${categoryName}_${difficultyName}_1`;
      }

      // Use remove question from category endpoint
      await consoleService.removeQuizQuestionFromCategory({
        category_id: categoryId,
        question_id: questionId
      });
      
      await fetchQuizManagementData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete question');
    }
  };

  // Reset form
  const resetQuestionForm = () => {
    setQuestionForm({
      question: '',
      type: QuestionType.MULTIPLE_CHOICE,
      category: QuestionCategory.GENERAL_KNOWLEDGE,
      difficulty: QuestionDifficulty.EASY,
      options: [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ],
      points: 10,
      timeLimit: 30,
      tags: [],
      language: 'en'
    });
    setQuestionId('');
    setHintText('');
    setSelectedCategoryId('');
  };

  // Toggle category expansion - switches to Questions tab with filter
  const toggleCategoryExpansion = (categoryId: string) => {
    setFilterCategoryId(categoryId);
    setActiveTab('questions');
  };

  // Clear category filter
  const clearCategoryFilter = () => {
    setFilterCategoryId(null);
  };

  // Update option
  const updateOption = (index: number, field: 'text' | 'isCorrect', value: string | boolean) => {
    const newOptions = [...(questionForm.options || [])];
    if (field === 'isCorrect' && value === true) {
      // Only one option can be correct for multiple choice
      newOptions.forEach((opt, i) => {
        opt.isCorrect = i === index;
      });
    } else {
      newOptions[index] = { ...newOptions[index], [field]: value };
    }
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  useEffect(() => {
    fetchQuizManagementData();
  }, []);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      loadLeaderboardData();
    }
  }, [activeTab]);

  const loadLeaderboardData = async () => {
    try {
      setLeaderboardLoading(true);
      const data = await consoleService.getQuizLeaderboard(QUIZ_LEADERBOARD_ID);
      setLeaderboardRecords(data.records || []);
    } catch (err) {
      console.error('Failed to load quiz leaderboard:', err);
      setLeaderboardRecords([]);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const handleDeleteRecord = (record: LeaderboardRecord, index: number) => {
    openConfirmDialog(
      'Remove Record',
      `Are you sure you want to remove the record for "${record.username || record.owner_id}"?`,
      async () => {
        try {
          await consoleService.deleteLeaderboardRecord('', QUIZ_LEADERBOARD_ID, record.owner_id);
          setLeaderboardRecords(prev => prev.filter((_, i) => i !== index));
          setSuccessMessage('Record removed successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to remove record');
        }
      }
    );
  };

  const handleResetLeaderboard = () => {
    openConfirmDialog(
      'Reset Leaderboard',
      'Are you sure you want to reset the Quiz leaderboard? This will delete all records.',
      async () => {
        setResetting(true);
        try {
          await consoleService.leaderboardReset(QUIZ_LEADERBOARD_ID);
          await loadLeaderboardData();
          setSuccessMessage('Leaderboard reset successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to reset leaderboard');
        } finally {
          setResetting(false);
        }
      }
    );
  };

  const formatScore = (score: string): string => {
    return parseInt(score).toLocaleString();
  };

  const getRankIcon = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get category info
  const getCategoryInfo = (category: QuestionCategory) => {
    const categoryMap = {
      [QuestionCategory.GENERAL_KNOWLEDGE]: { name: 'General Knowledge', icon: '🧠', color: '#007bff' },
      [QuestionCategory.SPORTS]: { name: 'Sports', icon: '⚽', color: '#28a745' },
      [QuestionCategory.TECHNOLOGY]: { name: 'Technology', icon: '💻', color: '#6f42c1' },
      [QuestionCategory.SCIENCE]: { name: 'Science', icon: '🔬', color: '#20c997' },
      [QuestionCategory.HISTORY]: { name: 'History', icon: '🏛️', color: '#fd7e14' },
      [QuestionCategory.GEOGRAPHY]: { name: 'Geography', icon: '🌍', color: '#17a2b8' },
      [QuestionCategory.ENTERTAINMENT]: { name: 'Entertainment', icon: '🎬', color: '#e83e8c' },
      [QuestionCategory.LITERATURE]: { name: 'Literature', icon: '📚', color: '#6c757d' },
      [QuestionCategory.MATHEMATICS]: { name: 'Mathematics', icon: '🔢', color: '#ffc107' },
      [QuestionCategory.POLITICS]: { name: 'Politics', icon: '🏛️', color: '#dc3545' }
    };
    return categoryMap[category] || { name: category, icon: '❓', color: '#6c757d' };
  };

  // Get difficulty info
  const getDifficultyInfo = (difficulty: QuestionDifficulty) => {
    const difficultyMap = {
      [QuestionDifficulty.EASY]: { name: 'Easy', color: '#28a745', icon: '🟢' },
      [QuestionDifficulty.MEDIUM]: { name: 'Medium', color: '#ffc107', icon: '🟡' },
      [QuestionDifficulty.HARD]: { name: 'Hard', color: '#dc3545', icon: '🔴' }
    };
    return difficultyMap[difficulty];
  };

  if (loading) {
    return (
      <div className="quiz-management-container">
        <div className="quiz-management-header">
          <h2 className="quiz-management-title">Quiz Management</h2>
        </div>
        <div className="loading-state">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading quiz management...</span>
          </div>
          <p>Loading quiz management data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-management-container">
        <div className="quiz-management-header">
          <h2 className="quiz-management-title">Quiz Management</h2>
        </div>
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button className="btn btn-primary ms-3" onClick={fetchQuizManagementData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="quiz-management-container">
        <div className="quiz-management-header">
          <h2 className="quiz-management-title">Quiz Management</h2>
        </div>
        <div className="alert alert-info">
          No quiz management data available.
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-management-container">
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab('questions')}
        >
          ❓ Questions
        </button>
        <button 
          className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          📂 Categories
        </button>
        <button 
          className={`tab-button ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          🏆 Leaderboard
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Questions Tab */}
        {activeTab === 'questions' && (
          <div className="questions-tab">
            <div className="section-header">
              <h3>Question Bank</h3>
              <div className="header-actions">
                <div className="filter-dropdown">
                  <label>Filter by Category:</label>
                  <select
                    value={filterCategoryId || ''}
                    onChange={(e) => setFilterCategoryId(e.target.value || null)}
                    className="form-select"
                  >
                    <option value="">All Categories</option>
                    {Object.entries(backendCategories).map(([categoryId, categoryInfo]) => (
                      <option key={categoryId} value={categoryId}>
                        {categoryInfo.categoryName || categoryId}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImportQuestionsFile}
                  style={{ display: 'none' }}
                />
                <a
                  className="btn btn-outline-primary"
                  href={QUIZ_IMPORT_TEMPLATE_PATH}
                  download
                >
                  Download Excel Template
                </a>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImportingQuestions}
                >
                  {isImportingQuestions ? 'Importing...' : 'Import Excel'}
                </button>
                <button 
                  className="btn btn-success"
                  onClick={() => {
                    resetQuestionForm();
                    setSelectedCategoryId('');
                    setShowCreateModal(true);
                  }}
                >
                  ➕ Add Question
                </button>
              </div>
            </div>

            {importSummary && (
              <div className={`alert ${importSummary.failedRows > 0 ? 'alert-warning' : 'alert-success'}`}>
                <div>
                  Import result: {importSummary.importedRows}/{importSummary.totalRows} rows imported
                  {importSummary.failedRows > 0 ? `, ${importSummary.failedRows} failed.` : '.'}
                </div>
                {importSummary.failures.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    {importSummary.failures.slice(0, 5).map((failure) => (
                      <div key={`${failure.row}-${failure.message}`}>
                        Row {failure.row}: {failure.message}
                      </div>
                    ))}
                    {importSummary.failures.length > 5 && (
                      <div>+ {importSummary.failures.length - 5} more row errors</div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="questions-table">
              <table>
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Category</th>
                    <th>Difficulty</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.questions
                    .filter((question) => {
                      if (!filterCategoryId) return true;
                      // Filter by categoryId stored in question
                      const questionWithCategoryId = question as Question & { categoryId?: string };
                      // Debug: log the comparison
                      console.log(`Filter: ${filterCategoryId}, Question categoryId: ${questionWithCategoryId.categoryId}, Match: ${questionWithCategoryId.categoryId === filterCategoryId}`);
                      return questionWithCategoryId.categoryId === filterCategoryId;
                    })
                    .map((question) => {
                    const categoryInfo = getCategoryInfo(question.category);
                    const difficultyInfo = getDifficultyInfo(question.difficulty);
                    const questionWithCategoryId = question as Question & { categoryId?: string; categoryName?: string };
                    // Use stored categoryName from backend, or lookup from backendCategories, fallback to mapped name
                    const displayCategoryName = questionWithCategoryId.categoryName || 
                      (questionWithCategoryId.categoryId && backendCategories[questionWithCategoryId.categoryId]?.categoryName) || 
                      categoryInfo.name;
                    
                    return (
                      <tr key={question.id}>
                        <td className="question-cell">
                          <div className="question-preview">
                            {question.question.length > 60 
                              ? `${question.question.substring(0, 60)}...` 
                              : question.question
                            }
                          </div>
                          <div className="question-meta">
                            {question.points} pts • {question.timeLimit}s
                          </div>
                        </td>
                        <td>
                          <span 
                            className="category-badge clickable" 
                            style={{ backgroundColor: categoryInfo.color }}
                            onClick={() => questionWithCategoryId.categoryId && setFilterCategoryId(questionWithCategoryId.categoryId)}
                            title="Click to filter by this category"
                          >
                            {displayCategoryName}
                          </span>
                        </td>
                        <td>
                          <span className="difficulty-badge" style={{ color: difficultyInfo.color }}>
                            {difficultyInfo.icon} {difficultyInfo.name}
                          </span>
                        </td>

                        <td>
                          <span className={`status-badge ${question.status}`}>
                            {question.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>

                        <td className="actions-cell">
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteQuestion(question.id)}
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="categories-tab">
            <div className="section-header">
              <h3>Question Categories</h3>
              <p>Overview of question distribution across categories. You can tweak names and descriptions here for display purposes.</p>
              <div className="section-actions">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => {
                    setNewCategoryId('');
                    setNewCategoryName('');
                    setNewCategoryDifficulty(0);
                    setShowCategoryModal(true);
                  }}
                >
                  ➕ Add Category
                </button>
              </div>
            </div>

            <div className="categories-grid">
              {dashboardData.categories.map((category, index) => {
                const categoryInfo = getCategoryInfo(category.category);
                // Use actual name from backend if available, otherwise use mapped name
                const displayName = category.name || categoryInfo.name;
                // Get the categoryId for this category
                const categoryId = Object.entries(backendCategories).find(([_, info]) => info.categoryName === category.name)?.[0] || '';
                
                return (
                  <div key={`${category.category}-${index}`} className="category-card">
                    <div className="category-header">
                      <div className="category-info">
                          <h4>{displayName}</h4>
                      </div>
                    </div>

                    <div className="category-stats">
                      <div className="stat-row">
                        <span className="stat-label">Category ID:</span>
                        <span className="stat-value">{categoryId || 'N/A'}</span>
                      </div>
                      <div className="stat-row">
                        <span className="stat-label">Difficulty:</span>
                        <span className="stat-value">
                          {category.averageDifficulty === 0 ? 'Easy' : category.averageDifficulty === 1 ? 'Medium' : 'Hard'}
                        </span>
                      </div>
                    </div>

                    <div className="category-actions">
                      {categoryId && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => toggleCategoryExpansion(categoryId)}
                        >
                          📂 View Questions
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={async () => {
                          const displayName = category.name || getCategoryInfo(category.category).name;
                          if (!window.confirm(`Are you sure you want to delete "${displayName}"?`)) {
                            return;
                          }
                          
                          try {
                            // Get all backend categories to find matching categoryIds
                            const rpcResponse = await consoleService.callRpcFunction({
                              id: 'quiz_categories_get',
                              body: JSON.stringify({}),
                              user_id: '00000000-0000-0000-0000-000000000000',
                              http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
                            });

                            if (rpcResponse.body) {
                              const responseData = JSON.parse(rpcResponse.body);
                              // Find all categoryIds that match this category name
                              const categoryIdsToDelete = Object.keys(responseData.categories).filter(
                                (catId) => responseData.categories[catId].categoryName === category.name
                              );

                              if (categoryIdsToDelete.length === 0) {
                                throw new Error('No matching categories found to delete');
                              }

                              // Delete all matching categoryIds
                              for (const categoryId of categoryIdsToDelete) {
                                await consoleService.removeQuizCategory({ category_id: categoryId });
                              }

                              // Refresh all data from backend to ensure full sync
                              await fetchQuizManagementData();
                            }
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Failed to delete category');
                            console.error('Error deleting category:', err);
                          }
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === 'leaderboard' && (
          <div className="leaderboard-tab">
            <div className="leaderboard-header">
              <h3>🏆 Quiz Leaderboard</h3>
              <div className="leaderboard-actions">
                <button
                  className="btn btn-danger"
                  onClick={handleResetLeaderboard}
                  disabled={resetting}
                >
                  {resetting ? 'Resetting...' : '🔄 Reset Leaderboard'}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={loadLeaderboardData}
                  disabled={leaderboardLoading}
                >
                  {leaderboardLoading ? 'Loading...' : '🔄 Refresh'}
                </button>
              </div>
            </div>

            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {leaderboardLoading ? (
              <div className="loading-spinner">Loading leaderboard...</div>
            ) : leaderboardRecords.length > 0 ? (
              <div className="leaderboard-table">
                <div className="table-header">
                  <div className="header-cell rank-cell">Rank</div>
                  <div className="header-cell player-cell">Player</div>
                  <div className="header-cell score-cell">Score</div>
                  <div className="header-cell date-cell">Last Updated</div>
                  <div className="header-cell actions-cell">Actions</div>
                </div>
                <div className="table-body">
                  {leaderboardRecords.map((record, index) => {
                    const rank = record.rank || (index + 1);
                    return (
                      <div key={record.owner_id} className={`table-row ${rank <= 3 ? 'top-rank' : ''}`}>
                        <div className="table-cell rank-cell">
                          <span className="rank-icon">{getRankIcon(rank)}</span>
                          <span className="rank-number">#{rank}</span>
                        </div>
                        <div className="table-cell player-cell">
                          <div className="player-info">
                            <div className="player-name">{record.username || 'Anonymous'}</div>
                            <div className="player-id">ID: {record.owner_id.substring(0, 8)}...</div>
                          </div>
                        </div>
                        <div className="table-cell score-cell">
                          <div className="score-value">{formatScore(record.score)}</div>
                        </div>
                        <div className="table-cell date-cell">
                          <div className="update-time">{formatDate(record.update_time)}</div>
                        </div>
                        <div className="table-cell actions-cell">
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteRecord(record, index)}
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="empty-leaderboard">
                <div className="empty-icon">🏆</div>
                <h3>No Rankings Yet</h3>
                <p>No players have earned coins in Quiz yet.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Question Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>Create New Question</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Question Text *</label>
                <textarea
                  value={questionForm.question}
                  onChange={(e) => setQuestionForm({...questionForm, question: e.target.value})}
                  className="form-control"
                  placeholder="Enter your question here..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Select a category</option>
                    {Object.entries(backendCategories).map(([catId, catInfo]) => (
                      <option key={catId} value={catId}>
                        {catInfo.categoryName} ({catId})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Difficulty *</label>
                  <select 
                    value={questionForm.difficulty} 
                    onChange={(e) => setQuestionForm({...questionForm, difficulty: e.target.value as QuestionDifficulty})}
                    className="form-control"
                  >
                    <option value={QuestionDifficulty.EASY}>Easy (0)</option>
                    <option value={QuestionDifficulty.MEDIUM}>Medium (1)</option>
                    <option value={QuestionDifficulty.HARD}>Hard (2)</option>
                  </select>
                </div>
              </div>

              <div className="options-section">
                <label>Answer Options * (select correct answer)</label>
                {questionForm.options?.map((option: { text: string; isCorrect: boolean }, index: number) => (
                  <div key={index} className="option-row">
                    <input
                      type="radio"
                      name="correctOption"
                      checked={option.isCorrect}
                      onChange={() => updateOption(index, 'isCorrect', true)}
                    />
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => updateOption(index, 'text', e.target.value)}
                      className="form-control"
                      placeholder={`Option ${index + 1}`}
                    />
                  </div>
                ))}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Question ID</label>
                  <input
                    type="text"
                    value={questionId}
                    onChange={(e) => setQuestionId(e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Time Limit (seconds) *</label>
                  <input
                    type="number"
                    value={questionForm.timeLimit}
                    onChange={(e) => setQuestionForm({...questionForm, timeLimit: parseInt(e.target.value)})}
                    className="form-control"
                    min="5"
                    max="300"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Hint Text (optional)</label>
                <input
                  type="text"
                  value={hintText}
                  onChange={(e) => setHintText(e.target.value)}
                  className="form-control"
                  placeholder="Enter hint text..."
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCreateQuestion}
              >
                Create Question
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Category</h3>
              <button className="modal-close" onClick={() => {
                setShowCategoryModal(false);
                setNewCategoryId('');
                setNewCategoryName('');
                setNewCategoryDifficulty(0);
              }}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Category ID *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newCategoryId}
                  onChange={(e) => setNewCategoryId(e.target.value)}
                  placeholder="e.g., science_easy_1"
                />
              </div>

              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Science"
                />
              </div>

              <div className="form-group">
                <label>Difficulty *</label>
                <select
                  className="form-control"
                  value={newCategoryDifficulty}
                  onChange={(e) => setNewCategoryDifficulty(parseInt(e.target.value))}
                >
                  <option value={0}>🟢 Easy (0)</option>
                  <option value={1}>🟡 Medium (1)</option>
                  <option value={2}>🔴 Hard (2)</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowCategoryModal(false);
                  setNewCategoryId('');
                  setNewCategoryName('');
                  setNewCategoryDifficulty(0);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  if (!newCategoryId || !newCategoryName) {
                    setError('Please fill in Category ID and Category Name');
                    return;
                  }
                  
                  try {
                    await consoleService.addQuizCategory({
                      categoryId: newCategoryId,
                      categoryName: newCategoryName,
                      difficulty: newCategoryDifficulty,
                      isActive: true
                    });

                    await fetchQuizManagementData();
                    setShowCategoryModal(false);
                    setNewCategoryId('');
                    setNewCategoryName('');
                    setNewCategoryDifficulty(0);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to add category');
                    console.error('Error adding category:', err);
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default QuizManagement;
